import axios, { AxiosInstance, AxiosError } from 'axios';
import config from '../config';
import { checkVersionOnApiRequest } from './versionManager';

const { api } = config;

/**
 * Creates a configured axios instance with standardized interceptors
 * for authentication and ApiResponse handling.
 *
 * This centralizes the axios configuration to avoid DRY violations across service files.
 */
export const createApiAxiosInstance = (baseURL: string = `${api.API_URL}/api`): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Add auth token and tenant subdomain to all requests
  instance.interceptors.request.use(
    (config) => {
      // Add authentication token
      const authUser = sessionStorage.getItem('authUser');
      if (authUser) {
        const user = JSON.parse(authUser);
        if (user && user.token) {
          config.headers.Authorization = `Bearer ${user.token}`;
        }
      }

      // Extract tenant subdomain from hostname and send as header
      // Example: promedico.atoscloud.app → X-Tenant-Subdomain: promedico
      const hostname = window.location.hostname;
      const parts = hostname.split('.');

      // Extract subdomain (first part of hostname)
      // Ignore common subdomains: www, api, admin
      if (parts.length >= 2) {
        const subdomain = parts[0].toLowerCase();
        if (subdomain !== 'www' && subdomain !== 'api' && subdomain !== 'admin' && subdomain !== 'localhost') {
          config.headers['X-Tenant-Subdomain'] = subdomain;
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - Preserve standardized ApiResponse structure and check version
  instance.interceptors.response.use(
    // Success handler - unwrap response.data and check version
    (response) => {
      // Check for version updates on every successful API response (throttled internally)
      checkVersionOnApiRequest().catch(err => {
        console.error('[Axios] Version check failed:', err);
      });
      return response.data;
    },

    // Error handler - preserve ApiResponse or convert to Error
    (error: AxiosError) => {
      // Check for version updates even on errors (user is still active)
      checkVersionOnApiRequest().catch(err => {
        console.error('[Axios] Version check failed:', err);
      });

      // If response has ApiResponse structure with severity, RESOLVE it so it goes to .then() not .catch()
      // This allows handleApiResponse to be called in the try block, enabling toast notifications
      if (error.response?.data &&
          typeof error.response.data === 'object' &&
          'severity' in error.response.data) {
        return Promise.resolve(error.response.data); // Resolve with ApiResponse instead of rejecting
      }

      // Fallback for non-standardized errors
      const message = (error.response?.data as any)?.message || error.message || 'Erro na requisição';
      return Promise.reject(new Error(message));
    }
  );

  return instance;
};

/**
 * Default axios instance for general API calls
 */
export const apiAxios = createApiAxiosInstance();
